import React, { useState, useEffect, useCallback, useRef } from 'react';
import { staffApi, leaveTypesApi } from '../../services/api';
import toast from 'react-hot-toast';
import LeaveBalanceSummary from '../../components/Leave/LeaveBalanceSummary';
import { usePermissions } from '../../hooks/usePermissions';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDate, isToday, isSunday } from 'date-fns';
import { HiOutlineCalendar, HiOutlineDocumentReport, HiOutlineUsers, HiOutlineTrash } from 'react-icons/hi';
import { getCalendarNowIST, formatMonthLongFromIndex0 } from '../../utils/timezoneDisplay';

const countMonthAttendanceMarks = (attendanceByDay) =>
  Object.values(attendanceByDay || {}).filter((a) => a?.id).length;

const formatJoinDateYmd = (val) => {
  if (!val) return null;
  const s = String(val).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
};

const Attendance = () => {
  const { canDelete, isAdmin } = usePermissions();
  const [register, setRegister] = useState(null);
  const [attendanceReport, setAttendanceReport] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const initialLoadDoneRef = useRef(false);
  const [selectedMonth, setSelectedMonth] = useState(() => getCalendarNowIST().month);
  const [selectedYear, setSelectedYear] = useState(() => getCalendarNowIST().year);
  const [showMarkModal, setShowMarkModal] = useState(false);
  const [markingDate, setMarkingDate] = useState(null);
  const [markingRecords, setMarkingRecords] = useState([]);
  const [markingFocusStaffId, setMarkingFocusStaffId] = useState(null);
  const [markingFocusStaffName, setMarkingFocusStaffName] = useState('');
  const [showAllStaffInModal, setShowAllStaffInModal] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [view, setView] = useState('register'); // 'register' | 'summary'
  const [markModalScope, setMarkModalScope] = useState('day'); // 'day' | 'month'
  const [monthMarkSkipSundays, setMonthMarkSkipSundays] = useState(true);
  const [monthSaveLoading, setMonthSaveLoading] = useState(false);
  const [clearingAttendanceId, setClearingAttendanceId] = useState(null);
  const [clearingAllStaffId, setClearingAllStaffId] = useState(null);

  const loadRegister = useCallback(async ({ isInitial = false } = {}) => {
    if (isInitial) setInitialLoading(true);
    else setRefreshing(true);
    try {
      const registerRes = await staffApi.getDailyRegister({ month: selectedMonth, year: selectedYear });
      if (registerRes.data.success) {
        setRegister(registerRes.data.data);
      }
    } catch (error) {
      console.error('Fetch register error:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch attendance register');
    } finally {
      if (isInitial) setInitialLoading(false);
      else setRefreshing(false);
    }
  }, [selectedMonth, selectedYear]);

  const loadReport = useCallback(async () => {
    setReportLoading(true);
    try {
      const reportRes = await staffApi.getAttendanceReport({ month: selectedMonth, year: selectedYear });
      if (reportRes.data.success) {
        setAttendanceReport(reportRes.data.data);
      }
    } catch (error) {
      console.error('Fetch report error:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch attendance summary');
    } finally {
      setReportLoading(false);
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    fetchLeaveTypes();
    const isInitial = !initialLoadDoneRef.current;
    loadRegister({ isInitial }).then(() => {
      initialLoadDoneRef.current = true;
    });
  }, [selectedMonth, selectedYear, loadRegister]);

  useEffect(() => {
    if (view === 'summary') {
      loadReport();
    }
  }, [view, selectedMonth, selectedYear, loadReport]);

  const fetchLeaveTypes = async () => {
    try {
      const response = await leaveTypesApi.getActive();
      setLeaveTypes(response.data.data);
    } catch (error) {
      console.error('Failed to fetch leave types:', error);
      setLeaveTypes([]);
    }
  };

  const refreshAfterSave = async () => {
    await loadRegister({ isInitial: false });
    if (view === 'summary') {
      loadReport();
    }
  };

  const isBeforeJoinDate = (staff, dayNum) => {
    const joinDate = formatJoinDateYmd(staff?.join_date);
    if (!joinDate) return false;
    const cellDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
    return cellDate < joinDate;
  };

  const buildMarkingRecordsForDate = (date, staffFilterId = null) => {
    if (!register) return [];
    const records = register.register
      .filter((item) => !isBeforeJoinDate(item.staff, date))
      .map((item) => {
        const dayAttendance = item.attendance[date];
        let leaveType = dayAttendance?.leave_type || '';
        if (leaveType === 'paid') leaveType = 'earned';
        return {
          user_id: item.staff.id,
          staff_name: item.staff.full_name,
          join_date: item.staff.join_date || null,
          attendance_id: dayAttendance?.id || null,
          has_existing_attendance: !!dayAttendance?.id,
          status: dayAttendance?.status || 'absent',
          check_in: dayAttendance?.check_in || '',
          check_out: dayAttendance?.check_out || '',
          leave_type: leaveType,
          notes: dayAttendance?.notes || '',
        };
      });
    if (staffFilterId != null) {
      return records.filter((r) => r.user_id === staffFilterId);
    }
    return records;
  };

  const openMarkModal = ({ date, staffId = null, staffName = '', allStaff = false }) => {
    const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(date).padStart(2, '0')}`;
    const records = buildMarkingRecordsForDate(date, allStaff ? null : staffId);
    if (records.length === 0) {
      toast('No staff are eligible for attendance on this date (before join date).', { icon: 'ℹ️' });
      return;
    }
    setMarkingDate(dateStr);
    setMarkingFocusStaffId(staffId);
    setMarkingFocusStaffName(staffName);
    setShowAllStaffInModal(allStaff);
    setMarkingRecords(records);
    setMarkModalScope('day');
    setMonthMarkSkipSundays(true);
    setShowMarkModal(true);
  };

  const handleDateClick = (date) => {
    openMarkModal({ date, allStaff: true });
  };

  const handleStaffAttendanceClick = (date, staff) => {
    if (isBeforeJoinDate(staff, date)) {
      const joinDate = formatJoinDateYmd(staff.join_date);
      toast(`Attendance starts from ${joinDate ? format(new Date(`${joinDate}T12:00:00`), 'dd-MM-yyyy') : 'join date'}`, {
        icon: 'ℹ️',
      });
      return;
    }
    openMarkModal({
      date,
      staffId: staff.id,
      staffName: staff.full_name,
      allStaff: false,
    });
  };

  const handleShowAllStaffToggle = (enabled) => {
    if (!markingDate || !register) return;
    const dayNum = parseInt(markingDate.split('-')[2], 10);
    setShowAllStaffInModal(enabled);
    if (enabled) {
      setMarkingRecords(buildMarkingRecordsForDate(dayNum));
    } else if (markingFocusStaffId != null) {
      setMarkingRecords(buildMarkingRecordsForDate(dayNum, markingFocusStaffId));
    }
  };

  const getRecordsToSave = () => {
    const base = showAllStaffInModal
      ? markingRecords
      : markingRecords.filter((r) => r.user_id === markingFocusStaffId);
    return base.map((record) => ({
      user_id: record.user_id,
      status: record.status,
      leave_type: record.leave_type && String(record.leave_type).trim() !== '' ? record.leave_type : null,
      check_in: record.check_in && record.check_in.trim() !== '' ? record.check_in : null,
      check_out: record.check_out && record.check_out.trim() !== '' ? record.check_out : null,
      notes: record.notes && record.notes.trim() !== '' ? record.notes : null,
    }));
  };

  const handleBulkMark = async () => {
    try {
      const normalizedRecords = getRecordsToSave();
      if (normalizedRecords.length === 0) {
        toast.error('No staff selected to save');
        return;
      }

      if (markModalScope === 'month') {
        const monthLabel = format(new Date(selectedYear, selectedMonth - 1, 1), 'MMMM yyyy');
        const skipNote = monthMarkSkipSundays ? ' (Sundays skipped)' : '';
        const names = markingRecords
          .filter((r) => normalizedRecords.some((n) => n.user_id === r.user_id))
          .map((r) => r.staff_name)
          .slice(0, 3)
          .join(', ');
        const extra =
          normalizedRecords.length > 3 ? ` and ${normalizedRecords.length - 3} more` : '';
        const shouldProceed = window.confirm(
          `Apply "${normalizedRecords[0]?.status}" for ${normalizedRecords.length} staff member(s) for all days in ${monthLabel}${skipNote}?\n\n${names}${extra}\n\nExisting marks on those days will be overwritten.`
        );
        if (!shouldProceed) return;

        setMonthSaveLoading(true);
        await staffApi.bulkMarkAttendanceMonth({
          month: selectedMonth,
          year: selectedYear,
          skip_sundays: monthMarkSkipSundays,
          records: normalizedRecords,
        });
        toast.success('Full month attendance saved');
      } else {
        setMonthSaveLoading(true);
        await staffApi.bulkMarkAttendance({
          attendance_date: markingDate,
          records: normalizedRecords,
        });
        toast.success('Attendance marked successfully');
      }

      setShowMarkModal(false);
      window.dispatchEvent(new CustomEvent('staffAttendanceChanged'));
      await refreshAfterSave();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to mark attendance');
    } finally {
      setMonthSaveLoading(false);
    }
  };

  const handleClearAttendance = async (attendanceId, staffName) => {
    if (!canDelete('staff')) {
      toast.error('You do not have permission to clear attendance');
      return;
    }
    if (!attendanceId) return;
    const label = staffName ? ` for ${staffName}` : '';
    if (!window.confirm(`Remove attendance${label} on this date? The cell will be empty until you mark again.`)) {
      return;
    }
    try {
      setClearingAttendanceId(attendanceId);
      await staffApi.deleteAttendance(attendanceId);
      toast.success('Attendance cleared');
      await refreshAfterSave();
      window.dispatchEvent(new CustomEvent('staffAttendanceChanged'));
      setMarkingRecords((prev) =>
        prev.map((r) =>
          r.attendance_id === attendanceId
            ? {
                ...r,
                attendance_id: null,
                has_existing_attendance: false,
                status: 'absent',
                check_in: '',
                check_out: '',
                leave_type: '',
                notes: '',
              }
            : r
        )
      );
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not clear attendance');
    } finally {
      setClearingAttendanceId(null);
    }
  };

  const handleClearAllMonthAttendance = async (staffId, staffName, attendanceByDay) => {
    if (!isAdmin) {
      toast.error('Only administrators can clear a full month of attendance');
      return;
    }
    const markedCount = countMonthAttendanceMarks(attendanceByDay);
    if (markedCount === 0) {
      toast.error('No attendance marks to clear for this month');
      return;
    }
    const monthLabel = `${formatMonthLongFromIndex0(selectedMonth - 1)} ${selectedYear}`;
    if (
      !window.confirm(
        `Clear all ${markedCount} attendance mark(s) for ${staffName} in ${monthLabel}? This cannot be undone.`
      )
    ) {
      return;
    }
    try {
      setClearingAllStaffId(staffId);
      const res = await staffApi.clearMonthAttendance({
        user_id: staffId,
        month: selectedMonth,
        year: selectedYear,
      });
      toast.success(res.data?.message || 'Month attendance cleared');
      await refreshAfterSave();
      window.dispatchEvent(new CustomEvent('staffAttendanceChanged'));
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not clear month attendance');
    } finally {
      setClearingAllStaffId(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return 'bg-green-100 text-green-700 border-green-300';
      case 'absent': return 'bg-red-100 text-red-700 border-red-300';
      case 'half_day': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'leave': return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'holiday': return 'bg-purple-100 text-purple-700 border-purple-300';
      default: return 'bg-gray-100 text-gray-500 border-gray-300';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'present': return 'P';
      case 'absent': return 'A';
      case 'half_day': return 'HL';
      case 'leave': return 'L';
      case 'holiday': return 'H';
      default: return '-';
    }
  };

  const monthStart = startOfMonth(new Date(selectedYear, selectedMonth - 1));
  const monthlyLedgerLeaveType = leaveTypes.find(
    (lt) => lt.uses_monthly_ledger === 1 || lt.uses_monthly_ledger === true
  );
  const halfDayLeaveType = leaveTypes.find(
    (lt) =>
      (lt.allows_half_day === 1 || lt.allows_half_day === true) &&
      !(lt.uses_monthly_ledger === 1 || lt.uses_monthly_ledger === true)
  );
  const monthEnd = endOfMonth(new Date(selectedYear, selectedMonth - 1));
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  const staffAttendanceRate = (report) => {
    const present = parseInt(report.present_days, 10) || 0;
    const half = parseInt(report.half_days, 10) || 0;
    const absent = parseInt(report.absent_days, 10) || 0;
    const leave = parseInt(report.leave_days, 10) || 0;
    const total = present + half + absent + leave;
    if (total <= 0) return 0;
    return Math.round(((present + half) / total) * 100);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 sm:text-2xl">Staff Attendance</h2>
          <p className="text-sm text-gray-500 mt-1">Mark and view daily attendance for all staff</p>
          {(refreshing || reportLoading) ? (
            <p className="text-xs text-primary-600 mt-0.5">Updating…</p>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
            <button
              type="button"
              onClick={() => setView('register')}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
                view === 'register' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <HiOutlineCalendar className="mr-1.5 inline h-4 w-4" />
              Register
            </button>
            <button
              type="button"
              onClick={() => setView('summary')}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
                view === 'summary' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <HiOutlineDocumentReport className="mr-1.5 inline h-4 w-4" />
              Summary
            </button>
          </div>

          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-2 py-1">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
              className="border-0 bg-transparent px-2 py-1.5 text-sm focus:ring-0"
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {formatMonthLongFromIndex0(i)}
                </option>
              ))}
            </select>
            <span className="text-gray-300">|</span>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
              className="border-0 bg-transparent px-2 py-1.5 text-sm focus:ring-0"
            >
              {Array.from({ length: 5 }, (_, i) => {
                const year = new Date().getFullYear() - 2 + i;
                return (
                  <option key={year} value={year}>
                    {year}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
      </div>

      {view === 'register' ? (
      <>
      {/* Daily Register Table — sticky header row + sticky staff column (same as patient attendance) */}
      <div className={`card overflow-hidden p-0 transition-opacity ${refreshing ? 'opacity-70' : ''}`}>
        <div className="max-h-[min(70vh,calc(100dvh-16rem))] overflow-auto">
          <div className="min-w-full">
            {!register?.register?.length ? (
              <div className="py-12 text-center text-gray-500">
                <p>No staff found</p>
              </div>
            ) : (
              <table className="w-full min-w-[980px] border-separate border-spacing-0">
                <thead>
                  <tr>
                    <th className="sticky left-0 top-0 z-50 w-[220px] min-w-[220px] max-w-[220px] border-b border-r border-gray-200 bg-white p-3 text-left font-semibold text-gray-700 shadow-[4px_0_8px_-2px_rgba(15,23,42,0.12)]">
                      Staff Name
                    </th>
                    {daysInMonth.map((day) => {
                      const isTodayDate = isToday(day);
                      const isSundayDate = isSunday(day);
                      return (
                        <th
                          key={day.toISOString()}
                          className={`sticky top-0 z-40 min-w-[50px] cursor-pointer border-b border-gray-200 p-2 text-center font-semibold transition-colors hover:bg-gray-50 ${
                            isTodayDate ? 'bg-primary-50' : isSundayDate ? 'bg-red-50' : 'bg-white'
                          }`}
                          onClick={() => handleDateClick(getDate(day))}
                          title={`Click to mark attendance for ${format(day, 'MMM dd')}`}
                        >
                          <div className={`text-xs ${isSundayDate ? 'text-red-500' : 'text-gray-500'}`}>
                            {format(day, 'EEE')}
                          </div>
                          <div
                            className={`text-sm ${
                              isTodayDate ? 'font-bold text-primary-600' : isSundayDate ? 'text-red-600' : 'text-gray-700'
                            }`}
                          >
                            {getDate(day)}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {register.register.map((item) => (
                    <tr key={item.staff.id} className="group border-b border-gray-100 hover:bg-gray-50">
                      <td className="sticky left-0 z-30 w-[220px] min-w-[220px] max-w-[220px] border-b border-r border-gray-100 bg-white p-3 shadow-[4px_0_8px_-2px_rgba(15,23,42,0.12)] group-hover:bg-gray-50">
                        <div className="relative pr-8">
                          <p
                            className="font-medium text-gray-900 truncate"
                            title={item.staff.full_name}
                          >
                            {item.staff.full_name}
                          </p>
                          {item.staff.join_date && (
                            <p className="mt-0.5 text-[11px] text-gray-400">
                              Join {format(new Date(`${formatJoinDateYmd(item.staff.join_date)}T12:00:00`), 'dd-MM-yyyy')}
                            </p>
                          )}
                          {isAdmin && countMonthAttendanceMarks(item.attendance) > 0 ? (
                            <button
                              type="button"
                              title="Clear all attendance this month"
                              disabled={clearingAllStaffId === item.staff.id}
                              onClick={() =>
                                handleClearAllMonthAttendance(
                                  item.staff.id,
                                  item.staff.full_name,
                                  item.attendance
                                )
                              }
                              className="absolute right-0 top-0 flex h-7 w-7 items-center justify-center rounded-full border border-rose-200 bg-white text-rose-600 shadow-sm hover:bg-rose-50 disabled:opacity-50"
                            >
                              {clearingAllStaffId === item.staff.id ? (
                                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-rose-500 border-t-transparent" />
                              ) : (
                                <HiOutlineTrash className="h-4 w-4" />
                              )}
                            </button>
                          ) : null}
                        </div>
                      </td>
                      {daysInMonth.map((day) => {
                        const dayNum = getDate(day);
                        const attendance = item.attendance[dayNum];
                        const isSundayDate = isSunday(day);
                        const beforeJoin = isBeforeJoinDate(item.staff, dayNum);
                        const joinYmd = formatJoinDateYmd(item.staff.join_date);
                        return (
                          <td
                            key={day.toISOString()}
                            className={`border-b border-gray-100 p-1 text-center ${isSundayDate ? 'bg-red-50/50' : ''}`}
                            onClick={() => {
                              if (!beforeJoin) handleStaffAttendanceClick(dayNum, item.staff);
                            }}
                          >
                            {beforeJoin ? (
                              <div
                                className="inline-flex items-center justify-center w-9 h-9 cursor-not-allowed select-none"
                                title={
                                  joinYmd
                                    ? `Attendance starts from ${format(new Date(`${joinYmd}T12:00:00`), 'dd-MM-yyyy')}`
                                    : 'Attendance starts from join date'
                                }
                              >
                                <span className="block w-5 h-0.5 bg-red-500 rounded-full" />
                              </div>
                            ) : attendance ? (
                              <div className="relative inline-flex flex-col items-center justify-center gap-0.5 max-w-[56px]">
                                {canDelete('staff') && attendance.id ? (
                                  <button
                                    type="button"
                                    title="Clear attendance"
                                    disabled={clearingAttendanceId === attendance.id}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleClearAttendance(attendance.id, item.staff.full_name);
                                    }}
                                    className="absolute -right-1.5 -top-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full border border-rose-200 bg-white text-rose-600 shadow-sm hover:bg-rose-50 disabled:opacity-50"
                                  >
                                    {clearingAttendanceId === attendance.id ? (
                                      <span className="h-2.5 w-2.5 animate-spin rounded-full border border-rose-500 border-t-transparent" />
                                    ) : (
                                      <HiOutlineTrash className="h-3 w-3" />
                                    )}
                                  </button>
                                ) : null}
                                <div
                                  role="button"
                                  tabIndex={0}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStaffAttendanceClick(dayNum, item.staff);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      handleStaffAttendanceClick(dayNum, item.staff);
                                    }
                                  }}
                                  className={`inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border text-xs font-semibold transition-all hover:scale-105 hover:opacity-80 ${getStatusColor(attendance.status)}`}
                                  title={
                                    attendance.status === 'leave' && attendance.leave_type
                                      ? `Leave: ${attendance.leave_type}${attendance.check_in ? ` - ${attendance.check_in}` : ''}${attendance.check_out ? ` to ${attendance.check_out}` : ''}`
                                      : `${attendance.status.charAt(0).toUpperCase() + attendance.status.slice(1).replace('_', ' ')}${attendance.check_in ? ` - ${attendance.check_in}` : ''}${attendance.check_out ? ` to ${attendance.check_out}` : ''}`
                                  }
                                >
                                  {getStatusLabel(attendance.status)}
                                </div>
                              </div>
                            ) : (
                              <div
                                role="button"
                                tabIndex={0}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStaffAttendanceClick(dayNum, item.staff);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleStaffAttendanceClick(dayNum, item.staff);
                                  }
                                }}
                                className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-gray-200 text-gray-400 transition-colors hover:bg-gray-50"
                              >
                                -
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="mt-0 flex flex-wrap gap-4 border-t border-gray-200 px-4 pb-4 pt-4 text-sm">
          <span className="font-medium text-gray-500">Legend:</span>
          <div className="flex items-center gap-1.5">
            <span className="inline-flex h-6 w-6 items-center justify-center">
              <span className="block w-4 h-0.5 bg-red-500 rounded-full" />
            </span>
            <span className="text-gray-600">Before join date</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="flex h-6 w-6 items-center justify-center rounded bg-green-100 text-xs font-semibold text-green-700">
              P
            </span>
            <span className="text-gray-600">Present</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="flex h-6 w-6 items-center justify-center rounded bg-red-100 text-xs font-semibold text-red-700">
              A
            </span>
            <span className="text-gray-600">Absent</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="flex h-6 w-6 items-center justify-center rounded bg-yellow-100 text-xs font-semibold text-yellow-700">
              HL
            </span>
            <span className="text-gray-600">Half Day</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="flex h-6 w-6 items-center justify-center rounded bg-blue-100 text-xs font-semibold text-blue-700">
              L
            </span>
            <span className="text-gray-600">Leave</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="flex h-6 w-6 items-center justify-center rounded bg-purple-100 text-xs font-semibold text-purple-700">
              H
            </span>
            <span className="text-gray-600">Holiday</span>
          </div>
          <p className="w-full text-xs text-gray-500 sm:ml-auto sm:w-auto">
            Click a day header to mark all staff · click a cell to mark one staff member
          </p>
        </div>
      </div>
      </>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {reportLoading ? (
            <div className="col-span-full flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent" />
            </div>
          ) : attendanceReport.length === 0 ? (
            <div className="col-span-full py-12 text-center text-gray-500">
              <HiOutlineUsers className="mx-auto mb-2 h-12 w-12 text-gray-300" />
              <p>No attendance records found for this month</p>
            </div>
          ) : (
            attendanceReport.map((report) => {
              const present = parseInt(report.present_days, 10) || 0;
              const absent = parseInt(report.absent_days, 10) || 0;
              const half = parseInt(report.half_days, 10) || 0;
              const leave = parseInt(report.leave_days, 10) || 0;
              const totalDays = present + absent + half + leave;
              const rate = staffAttendanceRate(report);

              return (
                <div key={report.id} className="card transition-shadow hover:shadow-md">
                  <div className="mb-4 flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{report.full_name}</h3>
                      {report.email && <p className="text-sm text-gray-500">{report.email}</p>}
                    </div>
                    <div
                      className={`rounded-full px-3 py-1 text-sm font-semibold ${
                        rate >= 80
                          ? 'bg-emerald-100 text-emerald-700'
                          : rate >= 60
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-rose-100 text-rose-700'
                      }`}
                    >
                      {rate}%
                    </div>
                  </div>

                  {totalDays > 0 && (
                    <div className="mb-4 flex h-2 overflow-hidden rounded-full bg-gray-100">
                      <div className="h-full bg-green-500" style={{ width: `${(present / totalDays) * 100}%` }} />
                      <div className="h-full bg-yellow-500" style={{ width: `${(half / totalDays) * 100}%` }} />
                      <div className="h-full bg-blue-500" style={{ width: `${(leave / totalDays) * 100}%` }} />
                      <div className="h-full bg-red-500" style={{ width: `${(absent / totalDays) * 100}%` }} />
                    </div>
                  )}

                  <div className="mb-4 grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full bg-green-500" />
                      <span className="text-gray-600">Present:</span>
                      <span className="font-semibold text-green-700">{present}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full bg-red-500" />
                      <span className="text-gray-600">Absent:</span>
                      <span className="font-semibold text-red-700">{absent}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full bg-yellow-500" />
                      <span className="text-gray-600">Half Day:</span>
                      <span className="font-semibold text-yellow-700">{half}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full bg-blue-500" />
                      <span className="text-gray-600">Leave:</span>
                      <span className="font-semibold text-blue-700">{leave}</span>
                    </div>
                  </div>

                  {report.monthly_leave_balance && (
                    <div className="border-t border-gray-200 pt-3">
                      <LeaveBalanceSummary balance={report.monthly_leave_balance} />
                    </div>
                  )}

                  {!report.monthly_leave_balance && report.leave_balance && (
                    <div className="border-t border-gray-200 pt-3">
                      <div className="mb-2 text-xs font-semibold text-gray-600">
                        Leave balance ({report.leave_balance.year})
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Total taken</span>
                        <span className="font-medium text-orange-600">
                          {parseFloat(report.leave_balance.total_taken || 0).toFixed(1)} days
                        </span>
                      </div>
                      <div className="mt-1 flex justify-between text-sm">
                        <span className="text-gray-600">Remaining</span>
                        <span
                          className={`font-medium ${
                            report.leave_balance.total_remaining === null
                              ? 'text-blue-600'
                              : report.leave_balance.total_remaining > 0
                                ? 'text-green-600'
                                : 'text-red-600'
                          }`}
                        >
                          {report.leave_balance.total_remaining === null
                            ? 'Unlimited'
                            : `${parseFloat(report.leave_balance.total_remaining).toFixed(1)} days`}
                        </span>
                      </div>
                    </div>
                  )}

                  {((report.leave_breakdown && report.leave_breakdown.length > 0) ||
                    report.leave_without_type > 0) && (
                    <div className="mt-3 border-t border-gray-200 pt-3">
                      <div className="mb-2 text-xs font-semibold text-gray-600">Leave details (this month)</div>
                      <div className="space-y-1">
                        {report.leave_breakdown?.map((leave, idx) => (
                          <div key={idx} className="flex justify-between text-xs">
                            <span className="text-gray-600">{leave.leave_type}</span>
                            <span className="font-medium text-gray-900">{leave.total_days} day(s)</span>
                          </div>
                        ))}
                        {report.leave_without_type > 0 && (
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-600">Leave (no type)</span>
                            <span className="font-medium text-gray-900">
                              {report.leave_without_type} day(s)
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Mark Attendance Modal */}
      {showMarkModal && (
        <div className="modal-shell flex items-center justify-center">
          <div className="modal-panel max-w-4xl">
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-lg font-semibold">
                    {markModalScope === 'month'
                      ? `Mark full month — ${format(new Date(selectedYear, selectedMonth - 1, 1), 'MMMM yyyy')}`
                      : `Mark Attendance — ${format(new Date(markingDate), 'MMMM dd, yyyy')}`}
                  </h3>
                  {!showAllStaffInModal && markingFocusStaffName && (
                    <p className="mt-1 text-sm font-medium text-primary-700">{markingFocusStaffName}</p>
                  )}
                  {markModalScope === 'month' && (
                    <p className="mt-1 text-xs text-gray-500">
                      Same status applied to every day in this month
                      {monthMarkSkipSundays ? ' (Sundays excluded)' : ''}
                      {' '}(days before each staff join date are skipped).
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-stretch sm:items-end gap-2 shrink-0">
                <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 self-end">
                  <button
                    type="button"
                    onClick={() => setMarkModalScope('day')}
                    className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-all ${
                      markModalScope === 'day'
                        ? 'bg-primary-600 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    This day
                  </button>
                  <button
                    type="button"
                    onClick={() => setMarkModalScope('month')}
                    className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-all ${
                      markModalScope === 'month'
                        ? 'bg-gray-900 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    Full month
                  </button>
                </div>
                {markModalScope === 'month' && (
                  <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer self-end">
                    <input
                      type="checkbox"
                      checked={monthMarkSkipSundays}
                      onChange={(e) => setMonthMarkSkipSundays(e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    Skip Sundays
                  </label>
                )}
                <label className="flex items-center gap-3 cursor-pointer select-none self-end">
                  <span className="text-sm font-medium text-gray-700">All staff</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={showAllStaffInModal}
                    onClick={() => handleShowAllStaffToggle(!showAllStaffInModal)}
                    className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                      showAllStaffInModal ? 'bg-primary-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                        showAllStaffInModal ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </label>
                </div>
              </div>
            </div>
            <div className="p-4 sm:p-6 overflow-y-auto max-h-[60vh]">
              <div className="space-y-4">
                {markingRecords.map((record, idx) => (
                  <div key={record.user_id} className="border border-gray-200 rounded-lg p-4">
                    <div className="mb-3 flex items-center justify-between gap-2">
                      {showAllStaffInModal ? (
                        <div className="font-medium text-gray-900">{record.staff_name}</div>
                      ) : (
                        <div />
                      )}
                      {canDelete('staff') && record.attendance_id && markModalScope === 'day' ? (
                        <button
                          type="button"
                          disabled={clearingAttendanceId === record.attendance_id}
                          onClick={() => handleClearAttendance(record.attendance_id, record.staff_name)}
                          className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                        >
                          <HiOutlineTrash className="h-3.5 w-3.5" />
                          Clear
                        </button>
                      ) : null}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Status</label>
                        <select
                          value={record.status}
                          onChange={(e) => {
                            const updated = [...markingRecords];
                            updated[idx].status = e.target.value;
                            if (e.target.value !== 'leave' && e.target.value !== 'half_day') {
                              updated[idx].leave_type = '';
                            } else if (e.target.value === 'half_day') {
                              updated[idx].leave_type = halfDayLeaveType ? halfDayLeaveType.name : '';
                            } else if (e.target.value === 'leave') {
                              updated[idx].leave_type = monthlyLedgerLeaveType ? monthlyLedgerLeaveType.name : '';
                            }
                            setMarkingRecords(updated);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="present">Present</option>
                          <option value="absent">Absent</option>
                          <option value="half_day">Half Day</option>
                          <option value="leave">Leave</option>
                          <option value="holiday">Holiday</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Check In</label>
                        <input
                          type="time"
                          value={record.check_in}
                          onChange={(e) => {
                            const updated = [...markingRecords];
                            updated[idx].check_in = e.target.value;
                            setMarkingRecords(updated);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          disabled={record.status === 'absent' || record.status === 'leave'}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Check Out</label>
                        <input
                          type="time"
                          value={record.check_out}
                          onChange={(e) => {
                            const updated = [...markingRecords];
                            updated[idx].check_out = e.target.value;
                            setMarkingRecords(updated);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          disabled={record.status === 'absent' || record.status === 'leave'}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Leave Type</label>
                        <select
                          value={record.leave_type || ''}
                          onChange={(e) => {
                            const updated = [...markingRecords];
                            updated[idx].leave_type = e.target.value || null;
                            setMarkingRecords(updated);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          disabled={record.status !== 'leave' && record.status !== 'half_day'}
                        >
                          <option value="">Select</option>
                          {leaveTypes.map((leaveType) => (
                            <option key={leaveType.id} value={leaveType.name}>
                              {leaveType.name}
                              {(leaveType.deduct_salary === 1 || leaveType.deduct_salary === true) 
                                ? ' (Deduct Salary)' 
                                : ' (Paid)'}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="mt-3">
                      <label className="block text-xs text-gray-500 mb-1">Notes</label>
                      <input
                        type="text"
                        value={record.notes}
                        onChange={(e) => {
                          const updated = [...markingRecords];
                          updated[idx].notes = e.target.value;
                          setMarkingRecords(updated);
                        }}
                        placeholder="Optional notes"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className={`p-4 sm:p-6 border-t border-gray-200 flex flex-col lg:flex-row items-stretch lg:items-center gap-3 ${showAllStaffInModal ? 'justify-between' : 'justify-end'}`}>
              {showAllStaffInModal && (
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => {
                    const updated = markingRecords.map(record => ({
                      ...record,
                      status: 'present',
                      leave_type: '',
                      check_in: '',
                      check_out: ''
                    }));
                    setMarkingRecords(updated);
                  }}
                  className="w-full sm:w-auto px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                >
                  Mark All Present
                </button>
                <button
                  onClick={() => {
                    const updated = markingRecords.map(record => ({
                      ...record,
                      status: 'absent',
                      leave_type: '',
                      check_in: '',
                      check_out: ''
                    }));
                    setMarkingRecords(updated);
                  }}
                  className="w-full sm:w-auto px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
                >
                  Mark All Absent
                </button>
              </div>
              )}
              <div className="flex flex-col-reverse sm:flex-row gap-2">
              <button
                type="button"
                onClick={() => setShowMarkModal(false)}
                disabled={monthSaveLoading}
                className="w-full sm:w-auto px-4 py-2.5 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBulkMark}
                disabled={monthSaveLoading}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {monthSaveLoading ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" aria-hidden />
                ) : null}
                {monthSaveLoading
                  ? 'Saving…'
                  : markModalScope === 'month'
                    ? 'Save full month'
                    : 'Save Attendance'}
              </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Attendance;

